import type { LineString, MultiLineString } from 'geojson';
import { type ComponentProps, useMemo } from 'react';

import { coordsToSVGPoints } from '~utils';

import { useSvgContext } from '.';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type PolylineProps = ComponentProps<'polyline'> & {
  /** GeoJSON LineString or MultiLineString geometry to render */
  geometry: LineString | MultiLineString;
  /** Title for accessibility */
  title?: string;
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Renders a GeoJSON LineString or MultiLineString geometry as one or more SVG polylines.
 * Handles coordinate transformation, geometry branching, and styling defaults.
 * Returns a fragment — the caller is responsible for any wrapping `<g>` element.
 *
 * @example
 * ```tsx
 * <Polyline
 *   geometry={feature.geometry}
 *   stroke="var(--color-violet-600)"
 *   strokeOpacity={0.5}
 *   className="my-polyline"
 * />
 * ```
 */
const Polyline = ({ geometry, title, ...rest }: PolylineProps) => {
  const { transformer } = useSvgContext();

  const lineCoordSets = useMemo(() => {
    if (!geometry || !transformer) return null;
    return geometry.type === 'MultiLineString' ? geometry.coordinates : [geometry.coordinates];
  }, [geometry, transformer]);

  if (!transformer || !lineCoordSets) return null;

  return (
    <>
      {lineCoordSets.map((coords, i) => (
        <polyline
          // biome-ignore lint/suspicious/noArrayIndexKey: stable sub-line order within a multi-line feature
          key={i}
          points={coordsToSVGPoints(
            coords.map(([x, y]) => [x, y] as [number, number]),
            transformer,
          )}
          fill="none"
          stroke="var(--color-veil)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          {...rest}
        >
          {title && <title>{title}</title>}
        </polyline>
      ))}
    </>
  );
};
Polyline.displayName = 'Polyline';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { PolylineProps };
export { Polyline };
